import {
  QueryFieldFilterConstraint,
  QueryLimitConstraint,
  QueryOrderByConstraint,
  QueryStartAtConstraint,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
} from 'firebase/firestore'

import { FirebaseCollection } from '@/constants/firebase-collection'
import { firestore } from '@/services/firebase'
import { getPathFileCompanyService } from '@/utils/storage'
import { delay } from '@/utils/tricks'

import { deleteFile } from '../file'

interface GetListProps {
  companyId: string
  setServiceList: (list: Service[] | undefined) => void
  setLoadingList: React.Dispatch<React.SetStateAction<boolean>>
  whereList?: QueryFieldFilterConstraint[]
  orderByList?: QueryOrderByConstraint[]
  startAfter?: QueryStartAtConstraint[]
  limit?: QueryLimitConstraint[]
  timeDelay?: number
}
export const getList = async ({
  companyId,
  setServiceList,
  setLoadingList,
  whereList,
  orderByList,
  startAfter,
  limit,
  timeDelay,
}: GetListProps) => {
  setLoadingList(true)

  if (timeDelay) await delay(timeDelay)

  const q = query(
    collection(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_SERVICE,
    ),
    ...(whereList || []),
    ...(orderByList || []),
    ...(startAfter || []),
    ...(limit || []),
  )
  onSnapshot(
    q,
    querySnapshot => {
      const serviceList: Service[] = []
      querySnapshot.forEach(doc => {
        const dataItem = { id: doc.id, ...doc.data() } as Service
        serviceList.push(dataItem)
      })
      setLoadingList(false)
      setServiceList(serviceList)
    },
    () => {
      console.log('Service - getList() - Error')
      setLoadingList(false)
    },
  )
}

interface GetOneProps {
  companyId: string
  serviceId: string
}
export const getOne = async ({
  companyId,
  serviceId,
}: GetOneProps): Promise<Service | undefined> => {
  const docRef = doc(
    firestore,
    FirebaseCollection.BASE_COMPANY,
    companyId,
    FirebaseCollection.BASE_SERVICE,
    serviceId,
  )
  const docSnap = await getDoc(docRef)

  try {
    if (docSnap.exists()) {
      return {
        id: serviceId,
        ...docSnap.data(),
      } as Service
    }
  } catch (error) {
    return undefined
  }

  return undefined
}

interface SaveProps {
  companyId: string
  values: Partial<Service>
}
export const save = async ({ companyId, values }: SaveProps) =>
  await addDoc(
    collection(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_SERVICE,
    ),
    {
      ...values,
      titleSearch: `${(values.title || '')
        .trimEnd()
        .trimStart()}${new Date().getTime()}`.toUpperCase(),
      clientFullnameSearch: `${(values.clientFullname || '')
        .trimEnd()
        .trimStart()}${new Date().getTime()}`.toUpperCase(),
      createdAt: new Date().getTime(),
      dateCreatedAt: new Date().setHours(0, 0, 0, 0),
      updatedAt: new Date().getTime(),
    },
  ).then(resp => resp.id)

interface updateProps {
  companyId: string
  serviceId: string
  values: Partial<Service>
}
export const update = async ({ companyId, serviceId, values }: updateProps) => {
  await setDoc(
    doc(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_SERVICE,
      serviceId,
    ),
    {
      ...values,
      titleSearch: `${(values.title || '')
        .trimEnd()
        .trimStart()}${new Date().getTime()}`.toUpperCase(),
      clientFullnameSearch: `${(values.clientFullname || '')
        .trimEnd()
        .trimStart()}${new Date().getTime()}`.toUpperCase(),
      ...(values.status === 'APPROVED'
        ? { assignServiceWorkerApprovedCreatedAt: new Date().getTime() }
        : {}),
      ...(values.status === 'AWAITING_APPROVAL'
        ? { assignServiceWorkerAwaitingApprovalCreatedAt: new Date().getTime() }
        : {}),
      updatedAt: new Date().getTime(),
    },
    { merge: true },
  )
}

interface RemoveProps {
  companyId: string
  serviceId: string
}
export const remove = async ({ companyId, serviceId }: RemoveProps) => {
  await deleteDoc(
    doc(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_SERVICE,
      serviceId,
    ),
  )
}

interface RemovePhotoProps {
  companyId: string
  serviceId: string
  photoName: string
}
export const removePhoto = async ({
  companyId,
  serviceId,
  photoName,
}: RemovePhotoProps) => {
  await deleteFile({
    pathFile: `${getPathFileCompanyService(companyId, serviceId)}/${photoName}`,
  })
}
